// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasShellContext } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Select from "@insite/mobius/Select";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import loadSystemList from "@mcmc/App/Handlers/CommonHandlers/LoadSystemList";
import loadContentPages from "@mcmc/App/Handlers/Pages/ProductList/LoadContentPages";
import { initializeScrollTo } from "@mcmc/App/Libraries";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getContentPageDataview } from "@mcmc/App/Store/Selectors/SearchResultsSelectors";
import { getSystemList } from "@mcmc/App/Store/Selectors/SystemListSelectors";
import InfoCard, { fields, InfoCardProps } from "@mcmc/App/Widgets/InfoCard/InfoCard.component";
import resourcesListStyles from "@mcmc/App/Widgets/ResourcesList/ResourcesListView.styles";
import Pagination from "@mcmc/MobiusOverrides/Pagination";
import Notify from "@mcmc/XDL/Components/Notify";
import { extendDataLayerWithEnvent } from "@mcmc/XDL/Utilities";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";

type State = {
    page?: number;
    pageSize?: number;
    category?: string;
    articleTypes?: string;
    firstLoad?: boolean;
};

const mapStateToProps = (state: McMcApplicationState) => {
    const location = getLocation(state);
    let parsedQuery: State = {};
    if (location && location.search) {
        parsedQuery = parseQueryString<State>(location.search);
    }
    const contentPageDataview = getContentPageDataview(state, "resourceList");

    return {
        location,
        contentPageDataview,
        categories: getSystemList(state, "ResourcesCategories"),
        articleTypes: getSystemList(state, "ArticleType"),
        parsedQuery,
    };
};

const mapDispatchToProps = {
    loadContentPages,
    loadSystemList,
    clearResources: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xContentPagesSearch/ClearResourcesView",
        } as any);
    },
};

type Props = HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    PageProps;

const styles = resourcesListStyles;

class ResourcesListView extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { firstLoad: true };
    }

    UNSAFE_componentWillMount(): void {
        if (!this.props.contentPageDataview?.value && !this.props.contentPageDataview?.isLoading) {
            this.loadData(this.props.parsedQuery);
            if (!this.props.categories) {
                this.props.loadSystemList({ name: "ResourcesCategories" });
            }
            if (!this.props.articleTypes) {
                this.props.loadSystemList({ name: "ArticleType" });
            }
        }
    }

    componentDidUpdate() {
        if (this.state.firstLoad) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ ...this.props.parsedQuery, firstLoad: false });
        }
    }

    componentWillUnmount() {
        this.props.clearResources();
    }

    buildQueryString(forAPI?: boolean, tmpState?: State) {
        const state = tmpState || this.state;
        let queryString = "";
        if (forAPI) {
            queryString += "searchType=resource&sort=date desc";
        }
        if (state.category) {
            queryString += `${queryString ? "&" : ""}category=${encodeURIComponent(state.category)}`;
        }
        if (state.articleTypes) {
            queryString += `${queryString ? "&" : ""}articleTypes=${encodeURIComponent(state.articleTypes)}`;
        }
        if (state.page) {
            queryString += `${queryString ? "&" : ""}page=${state.page}`;
        }
        if (state.pageSize) {
            queryString += `${queryString ? "&" : ""}pageSize=${state.pageSize}`;
        }
        return queryString;
    }

    loadData(tmpState?: State) {
        const { loadContentPages } = this.props;

        loadContentPages({
            queryString: this.buildQueryString(true, tmpState),
            isResourceListView: true,
        });
    }

    updatePageUrl = () => {
        let newUrl = `${this.props.location.pathname}`;
        const queryString = this.buildQueryString(false);
        newUrl += queryString ? `?${queryString}` : "";
        this.props.history.replace(newUrl);
    };

    handleChange = (key: string, value: string | number): void => {
        this.setState(
            {
                page: undefined,
                [key]: value,
            },
            () => {
                this.loadData();
                this.updatePageUrl();
            },
        );
    };

    handleDropdownChange = (event: React.FormEvent<HTMLSelectElement>): void => {
        const currentValue = event.currentTarget.value;
        const fieldName = event.currentTarget.name;

        this.handleChange(fieldName, currentValue);

        currentValue && extendDataLayerWithEnvent("resourcesfilter", {
            filterType: fieldName,
            filterValue: currentValue,
        })
    };

    changePage = (newPageIndex: number) => {
        this.handleChange("page", newPageIndex);
        initializeScrollTo("#resources-list");
    };

    changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        this.handleChange("pageSize", newPageSize);
        initializeScrollTo("#resources-list");
    };

    clearFilters = () => {
        this.setState({ category: "", articleTypes: "", page: undefined }, () => {
            this.loadData();
            this.updatePageUrl();
        });
    };

    render() {
        const categories = this.props.categories;
        const articleTypes = this.props.articleTypes;
        const isloading = this.props.contentPageDataview?.isLoading;
        const contents = this.props.contentPageDataview?.value?.contents;
        const pagination = this.props.contentPageDataview?.value?.pagination;
        let pageList;
        if (contents?.length) {
            pageList = contents.map((page, index) => {
                const infoCardProps: InfoCardProps = {
                    [fields.imageUrl]: page.openGraphImage,
                    [fields.imageCaption]: page.title,
                    [fields.copy]: page.pageSummary,
                    [fields.linkText]: "",
                    [fields.linkAlignment]: "left",
                    [fields.openInNewTab]: false,
                    url: page.url,
                    direction: "vertical",
                    maxNumberOfCopyRows: 4,
                    isSolutionsWidget: false
                };
                return (
                    // eslint-disable-next-line react/no-array-index-key
                    <GridItem key={`${page.title}_${page.url}_${index}`} {...styles.resourcesGridItem}>
                        <InfoCard {...infoCardProps}>
                            <StyledWrapper {...styles.infoCardChildren}>
                                <Typography className="tag">{page.publishedOn}</Typography>
                                {page.category && (
                                    <Link
                                        className="filter-link tag"
                                        onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            this.handleChange("category", page.category);
                                        }}
                                    >
                                        {page.category}
                                    </Link>
                                )}
                                {page.articleType && (
                                    <Link
                                        className="filter-link tag"
                                        onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            this.handleChange("articleTypes", page.articleType);
                                        }}
                                    >
                                        {page.articleType}
                                    </Link>
                                )}
                            </StyledWrapper>
                        </InfoCard>
                    </GridItem>
                );
            });
        }

        return (
            <StyledWrapper id="resources-list">
                <GridContainer {...styles.filterGridContainer}>
                    <GridItem {...styles.filterGridItem}>
                        <Select
                            label={translate("Filter By Category")}
                            value={this.state.category}
                            onChange={this.handleDropdownChange}
                            name="category"
                        >
                            <option value="">{translate("Show All")}</option>,
                            {categories?.values?.map((role: any) => (
                                <option key={role.name} value={role.name}>
                                    {role.description || role.name}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                    <GridItem {...styles.filterGridItem}>
                        <Select
                            label={translate("Filter By Type")}
                            value={this.state.articleTypes}
                            onChange={this.handleDropdownChange}
                            name="articleTypes"
                        >
                            <option value="">{translate("Show All")}</option>,
                            {articleTypes?.values?.map((role: any) => (
                                <option key={role.name} value={role.name}>
                                    {role.description || role.name}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                    <GridItem {...styles.filterGridItem}>
                        <Button variant="secondary" onClick={this.clearFilters}>
                            {translate("Clear Filters")}
                        </Button>
                    </GridItem>
                </GridContainer>
                {isloading && (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner} />
                    </StyledWrapper>
                )}
                {!isloading && pageList && <GridContainer {...styles.resourcesGridContainer}>{pageList}</GridContainer>}
                {!isloading && !pageList && (
                    <Notify
                        messageType="danger"
                        hideCloseButton={true}
                        body={siteMessage("McMc_Resources_NoArticlesFound")}
                    />
                )}
                {pagination && (
                    <Pagination
                        {...styles.pagination}
                        resultsCount={pagination!.totalItemCount}
                        currentPage={pagination!.page}
                        resultsPerPage={pagination!.pageSize}
                        resultsPerPageOptions={pagination!.pageSizeOptions}
                        onChangePage={this.changePage}
                        onChangeResultsPerPage={this.changeResultsPerPage}
                        pageSizeCookie="ResourcesList-PageSize"
                        totalCountLabelConfig={{ showTotal: true, pluralLabel: "Items", singularLabel: "Item" }}
                        hidePerPage={false}
                    />
                )}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(ResourcesListView)),
    definition: {
        group: "Common",
        displayName: "Resources List View",
    },
};

export default widgetModule;
